import DeclineModal from '@containers/decline-modal';
import {
    EndpointErrorCode,
    EndpointErrorResponse
} from '@dtos/endpoint-error';
import {
    continueOnboardingReview,
    finishOnboardingReview,
    FlowAction,
    onboardingFlowActions,
    previousPageOnboardingReview
} from '@flows/onboarding-review/navigation';
import { ClientTrackingCategory } from '@models/client-tracking-category';
import { Section2ValidationErrors } from '@models/onboarding-review/section2-validation-errors';
import CompleteI9Page from '@screens/review-section2/complete-i9/complete-i9-page';
import { IErrorReportingService } from '@services/error-reporting-service';
import {
    createDeclineModalStore,
    createDocumentDetailsChangeTrackingService,
    createErrorReportingService,
    createESignRequestService,
    createLoaderService,
    createSection1CandidateDetailsStore,
    createSection2CertificationStore,
    createSection2CompleteStore,
    createSection2DocumentsStore,
    createTranslationService
} from '@services/factories';
import { ILoaderService } from '@services/LoaderService';
import { IDocumentDetailsChangeTrackingService } from '@services/onboarding-review/document-details-change-tracking-service';
import {
    ESignResult,
    IESignRequestService
} from '@services/onboarding-review/e-sign-request-service';
import { IDeclineModalStore } from '@stores/modal/decline-modal-store';
import { ISection1CandidateDetailStore } from '@stores/onboarding-review/section1-candidate-detail-store';
import { ISection2CertificationStore } from '@stores/onboarding-review/section2-certification-store';
import { ISection2CompleteStore } from '@stores/onboarding-review/section2-complete-store';
import { ISection2DocumentsStore } from '@stores/onboarding-review/section2-documents-store';
import { cacheTranslations } from '@utils/cache-translations';
import TrackingService, { ITrackingService } from '@services/TrackingService';
import { AxiosError } from 'axios';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import {
    merge,
    Subscription
} from 'rxjs';
import { ClientTrackingName } from '@models/client-tracking-name';

enum CompleteFormI9Section2Translation {
    declineSection2Context = 'declineSection2Context'
}

// move to proper service/store later
export function checkESignRequestValidity(
    esignRequestService: IESignRequestService,
    documentDetailsChangeTrackingService: IDocumentDetailsChangeTrackingService
): boolean {
    const hasValidESignFields = esignRequestService.isRequestValid();
    const hasValidDocumentDetails = documentDetailsChangeTrackingService.isRequestValid();
    return hasValidESignFields && hasValidDocumentDetails;
}

function isAxiosError<T>(error: Error): error is AxiosError<T> {
    return (error as AxiosError).isAxiosError;
}

export function checkForDocumentNumberMismatchWithSection1(error: Error): boolean {
    if (isAxiosError<EndpointErrorResponse>(error)) {
        const errorCode = error.response?.data.errorCode;

        if (
            errorCode === EndpointErrorCode.DocumentNumberMismatchWithSection1
        ) {
            return true;
        }
    }

    return false;
}

@observer
class CompleteFormI9Section2 extends React.Component<RouteComponentProps> {
    // Probably good idea to dependency inject these later
    store: ISection2CompleteStore = createSection2CompleteStore();
    certificationStore: ISection2CertificationStore = createSection2CertificationStore();
    declineModalStore: IDeclineModalStore = createDeclineModalStore();
    candidateDetailsStore: ISection1CandidateDetailStore = createSection1CandidateDetailsStore();
    esignRequestService: IESignRequestService = createESignRequestService();
    documentsStore: ISection2DocumentsStore = createSection2DocumentsStore();
    loaderService: ILoaderService = createLoaderService();
    documentDetailsChangeTrackingService: IDocumentDetailsChangeTrackingService = createDocumentDetailsChangeTrackingService();
    errorReportingService: IErrorReportingService = createErrorReportingService();

    @observable
    eSignResult: ESignResult = ESignResult.InActive;

    @observable
    validationErrors: Record<Section2ValidationErrors, boolean> = {
        confirmAgreeToSign: false,
        confirmDocumentsPhysicallyExamined: false,
        confirmESignAgreement: false,
        documentDetailMissing: false,
        provideFirstName: false,
        provideLastName: false,
        provideSignDate: false,
        documentNumberDoesNotMatchSection1: false
    };

    readonly globalTranslations = cacheTranslations(
        createTranslationService(),
        CompleteFormI9Section2Translation
    );

    private onboardingFlowRouteSubscription: Subscription = Subscription.EMPTY;
    private readonly trackingService: ITrackingService = new TrackingService();
    componentDidMount() {
        this.onboardingFlowRouteSubscription = merge(
            continueOnboardingReview,
            finishOnboardingReview,
        ).subscribe(
            route => this.props.history.push(route),
            error => {
                console.error(error);
                // todo: display error feedback
            }
        );

        this.onboardingFlowRouteSubscription.add(
            previousPageOnboardingReview.subscribe(() => this.props.history.goBack())
        );

        this.store.initialise();
    }

    componentWillUnmount() {
        this.onboardingFlowRouteSubscription.unsubscribe();
    }

    showDeclineModal = () => this.declineModalStore.openModal();

    validateESignRequest = (): boolean => {
        this.validationErrors = {
            ...this.esignRequestService.getValidationErrors(),
            ...this.documentDetailsChangeTrackingService.getValidationErrors(),
            [Section2ValidationErrors.documentNumberDoesNotMatchSection1]: this.validationErrors.documentNumberDoesNotMatchSection1
        };

        this.errorReportingService.reportEvent({
            message: 'ESign Request Validation Errors',
            data: this.validationErrors,
            category: 'validation'
        });

        const isRequestValid = checkESignRequestValidity(this.esignRequestService, this.documentDetailsChangeTrackingService);

        this.errorReportingService.reportEvent({
            message: 'ESign Request Validation ' + isRequestValid,
            category: 'validation'
        });

        return isRequestValid;
    }

    triggerESignAsync = async (): Promise<ESignResult> => {
        if (!this.validateESignRequest()) {
            return ESignResult.InvalidRequest;
        }

        this.validationErrors.documentNumberDoesNotMatchSection1 = false;

        try {
            const succeeded = await this.documentDetailsChangeTrackingService.submitChangesAsync();
            this.trackingService.trackEvent(ClientTrackingCategory.DocumentDetailsChangeTracking, ClientTrackingName.Success);
            if (!succeeded) {
                this.trackingService.trackEvent(ClientTrackingCategory.DocumentDetailsChangeTracking, ClientTrackingName.Error);
                return ESignResult.Failed;
            }
        } catch (error: unknown) {
            if (checkForDocumentNumberMismatchWithSection1(error as Error)) {
                this.validationErrors.documentNumberDoesNotMatchSection1 = true;
                return ESignResult.InvalidRequest;
            }
            this.trackingService.trackEvent(ClientTrackingCategory.DocumentDetailsChangeTracking, ClientTrackingName.Error);
            return ESignResult.Failed;
        }

        return this.esignRequestService.requestSection2SignAsync();
    };

    handleESign = async () => {
        this.loaderService.showLoader();
        this.eSignResult = await this.triggerESignAsync();
        this.loaderService.hideLoader();

        switch (this.eSignResult) {
            case ESignResult.Success:
                onboardingFlowActions.next(FlowAction.ESign);
                this.trackingService.trackEvent(ClientTrackingCategory.ESignResultSuccess,ClientTrackingName.Success);
                break;
            case ESignResult.Failed:
                this.trackingService.trackEvent(ClientTrackingCategory.ESignResultFailure,ClientTrackingName.Error);
                break;
            case ESignResult.InvalidRequest:
                this.trackingService.trackEvent(ClientTrackingCategory.ESignResultInvalidRequest,ClientTrackingName.Error);
                break;
            default:
                break;
        }

        return false;
    };

    handleDeclineSuccess = () => onboardingFlowActions.next(FlowAction.Decline);

    // todo: extract this navigation logic to a wizard/multi step form logic
    handleGoToPreviousPage = () => onboardingFlowActions.next(FlowAction.GoBack);

    render() {
        return (
            <React.Fragment>
                <CompleteI9Page store={this.store}
                                candidateDetails={this.candidateDetailsStore}
                                certificationStore={this.certificationStore}
                                documentsStore={this.documentsStore}
                                eSignResult={this.eSignResult}
                                validationErrors={this.validationErrors}
                                validateForm={this.validateESignRequest}
                                onESign={this.handleESign}
                                onDecline={this.showDeclineModal}
                                onBack={this.handleGoToPreviousPage} />
                <DeclineModal store={this.declineModalStore}
                              onDeclineSuccess={this.handleDeclineSuccess}
                              trackingCategory={ClientTrackingCategory.Section2Decline}
                              declineQuestionContext={this.globalTranslations.declineSection2Context} />
            </React.Fragment>
        );
    }
}

export default CompleteFormI9Section2;



// WEBPACK FOOTER //
// ./src/screens/review-section2/complete-i9/index.tsx